import React from 'react';
import Parse from 'parse';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import NfcRoundedIcon from '@mui/icons-material/NfcRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import AudiotrackRoundedIcon from '@mui/icons-material/AudiotrackRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import { apiCapabilities, lifeTaggerApi } from '../api/lifetaggerApi';
import { lifeTaggerDesign } from '../design/tokens';
import LifeObjectRenderer from '../experiences/LifeObjectRenderer';
import { validateLifeObjectDraft } from './objectValidation';
import { withSelectedResource } from './objectResourceSelection';
import AudiencePicker from './AudiencePicker';
import { audiencePresentation } from './audiencePresentation';
import WebPushTrigger from './WebPushTrigger';
import LifeObjectComposer from './LifeObjectComposer';
import LifeBotSetup from './LifeBotSetup';
import { isLifeBot } from './lifeObjectType';
import {
  newPushDelivery,
  pushDeliveryError,
  localDeliveryTime,
} from './objectPushDelivery';

const emptyObject = {
  name: '',
  content: [
    {
      type: 'text',
      body: '',
      url: null,
      assetId: null,
      mimeType: 'text/plain',
      language: 'en',
      label: null,
    },
  ],
  audience: { visibility: 'public', identifiers: [] },
  lifecycle: {
    status: 'draft',
    startAt: null,
    endAt: null,
    recurrence: 'none',
    recurrenceWindow: null,
  },
  experiences: [{ renderer: 'web', config: { layout: 'story' } }],
  anchors: [],
  tags: [],
  metadata: {},
};

const placementLabels = {
  qr: 'LifeCode',
  'geo-point': 'Location',
  'geo-fence': 'Location area',
  nfc: 'LifeTap',
  ble: 'Nearby device',
  audio: 'Recognized audio',
  url: 'Web link',
  connector: 'Connected service',
  push: 'Push notification',
  availability: 'Time & availability',
};

const placementGuidance = {
  qr: 'Reveal this LifeTag when someone scans a LifeCode.',
  'geo-point': 'Reveal it at a specific place.',
  'geo-fence': 'Reveal it within an area around a place.',
  nfc: 'Reveal it when someone taps an NFC tag.',
  ble: 'Reveal it near a Bluetooth beacon.',
  audio: 'Reveal it when LifeTagger recognizes an audio fingerprint.',
  url: 'Reveal it from a dedicated contextual URL.',
  connector: 'Reveal it from a supported connected service.',
  push: 'Send a notification now or schedule it for your followers and subscribers.',
  availability: 'Choose dates, times and repeating availability windows.',
};

const placementIcons = {
  qr: <QrCode2RoundedIcon />,
  'geo-point': <PlaceRoundedIcon />,
  'geo-fence': <PlaceRoundedIcon />,
  nfc: <NfcRoundedIcon />,
  ble: <SensorsRoundedIcon />,
  audio: <GraphicEqRoundedIcon />,
  url: <LinkRoundedIcon />,
  connector: <LinkRoundedIcon />,
};

const contentIcons = {
  image: <ImageRoundedIcon />,
  video: <VideocamRoundedIcon />,
  audio: <AudiotrackRoundedIcon />,
  pdf: <PictureAsPdfRoundedIcon />,
  'url-embed': <LinkRoundedIcon />,
};

export default function ObjectEditor({
  open,
  lifeObject,
  initialAsset,
  initialDiscovery,
  onClose,
  onSave,
  saving = false,
}) {
  const [draft, setDraft] = React.useState(emptyObject);
  const [audienceData, setAudienceData] = React.useState({});
  const [delivery, setDelivery] = React.useState(newPushDelivery);
  const [mediaType, setMediaType] = React.useState('image');
  const [mediaUrl, setMediaUrl] = React.useState('');
  const [placementType, setPlacementType] = React.useState('qr');
  const [placementName, setPlacementName] = React.useState('');
  const [placementIdentifier, setPlacementIdentifier] = React.useState('');
  const [placementRadius, setPlacementRadius] = React.useState('100');
  const [error, setError] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState('content');
  const [editingLifeBot, setEditingLifeBot] = React.useState(false);
  const [resources, setResources] = React.useState([]);
  const [resourcesLoading, setResourcesLoading] = React.useState(false);
  const reusableResource = ['qr', 'ble', 'audio'].includes(placementType);
  const selectDiscoveryOption = (value) => {
    setDraft((current) =>
      withSelectedResource(
        current,
        placementType,
        placementIdentifier,
        placementName.trim() || placementLabels[placementType]
      )
    );
    setPlacementIdentifier('');
    setPlacementType(value);
  };
  React.useEffect(() => {
    if (!open || !reusableResource) return undefined;
    let active = true;
    setResources([]);
    setPlacementIdentifier('');
    setResourcesLoading(true);
    const request =
      placementType === 'qr'
        ? lifeTaggerApi.listLifeCodes({ limit: 1000 })
        : placementType === 'ble'
        ? lifeTaggerApi.listBluetoothBeacons({ limit: 1000 })
        : Parse.Cloud.run('getMedia');
    request
      .then((items) => {
        if (!active) return;
        setResources(
          (items || [])
            .map((item) => ({
              name: item.name || item.title || placementLabels[placementType],
              identifier: item.acrId || item.beaconId || item.identifier,
            }))
            .filter((item) => item.identifier)
        );
      })
      .catch(
        () =>
          active &&
          setError(
            'Could not load your discovery resources. Close and reopen to retry.'
          )
      )
      .finally(() => active && setResourcesLoading(false));
    return () => {
      active = false;
    };
  }, [open, placementType, reusableResource]);

  React.useEffect(() => {
    const next = lifeObject
      ? JSON.parse(JSON.stringify(lifeObject))
      : JSON.parse(JSON.stringify(emptyObject));
    if (!lifeObject && initialAsset) {
      const mimeType = initialAsset.mimeType || '';
      next.content.push({
        type: mimeType.startsWith('video/')
          ? 'video'
          : mimeType.startsWith('audio/')
          ? 'audio'
          : mimeType === 'application/pdf'
          ? 'pdf'
          : 'image',
        assetId: initialAsset.id,
        url: initialAsset.url,
        mimeType,
        label: initialAsset.name,
      });
    }
    setDraft(next);
    setDelivery(
      next.lifecycle?.status === 'draft' && next.metadata?.browserPush
        ? { ...newPushDelivery(), ...next.metadata.browserPush }
        : newPushDelivery()
    );
    setPlacementIdentifier('');
    setPlacementName('');
    setEditingLifeBot(isLifeBot(next));
    setError(null);
    setPlacementType(
      !lifeObject && initialDiscovery === 'push' ? 'push' : 'qr'
    );
    setActiveStep(
      !lifeObject && initialDiscovery === 'push' ? 'placements' : 'content'
    );
  }, [lifeObject, open, initialAsset, initialDiscovery]);

  const addMedia = () => {
    if (!mediaUrl.trim()) return;
    setDraft({
      ...draft,
      content: [
        ...draft.content,
        {
          type: mediaType,
          body: null,
          url: mediaUrl.trim(),
          assetId: null,
          mimeType: null,
          language: 'en',
          label: null,
        },
      ],
    });
    setMediaUrl('');
  };

  const removeMedia = (indexToRemove) => {
    setDraft({
      ...draft,
      content: draft.content.filter((_, index) => index !== indexToRemove),
    });
  };

  const moveMedia = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= draft.content.length) return;
    const content = [...draft.content];
    [content[index], content[nextIndex]] = [content[nextIndex], content[index]];
    setDraft({ ...draft, content });
  };

  const uploadMedia = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const inferredType = file.type.startsWith('image/')
      ? 'image'
      : file.type.startsWith('video/')
      ? 'video'
      : file.type.startsWith('audio/')
      ? 'audio'
      : file.type === 'application/pdf'
      ? 'pdf'
      : null;
    if (!inferredType) {
      setError('Choose an image, video, audio, or PDF file.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const asset = await lifeTaggerApi.uploadAsset(file);
      if (!asset.id) throw new Error('The upload did not return an Asset ID.');
      setDraft((current) => ({
        ...current,
        content: [
          ...current.content,
          {
            type: inferredType,
            body: null,
            url: null,
            assetId: asset.id,
            assetPreviewUrl: asset.url,
            mimeType: asset.mimeType,
            language: 'en',
            label: asset.name,
          },
        ],
      }));
    } catch (uploadError) {
      setError(uploadError.message || 'The media could not be uploaded.');
    } finally {
      setUploading(false);
    }
  };

  const addPlacement = () => {
    if (!placementIdentifier.trim()) return;
    if (
      (draft.anchors || []).some(
        (anchor) =>
          anchor.type === placementType &&
          anchor.identifier === placementIdentifier.trim()
      )
    ) {
      setError('This requirement is already on the LifeObject.');
      return;
    }
    let coordinates;
    if (placementType === 'geo-point') {
      const [latitude, longitude] = placementIdentifier
        .split(',')
        .map((value) => Number(value.trim()));
      if (
        !Number.isFinite(latitude) ||
        !Number.isFinite(longitude) ||
        Math.abs(latitude) > 90 ||
        Math.abs(longitude) > 180
      ) {
        setError('Enter the location as latitude, longitude.');
        return;
      }
      coordinates = { latitude, longitude };
    }
    if (placementType === 'url') {
      try {
        new URL(placementIdentifier.trim());
      } catch (_) {
        setError('Enter a complete web address, including https://.');
        return;
      }
    }
    const radiusMeters = Number(placementRadius);
    if (
      placementType === 'geo-fence' &&
      (!Number.isFinite(radiusMeters) || radiusMeters <= 0)
    ) {
      setError('Enter an area radius greater than zero.');
      return;
    }
    setDraft({
      ...draft,
      anchors: [
        ...(draft.anchors || []),
        {
          id: `draft-${Date.now()}`,
          name: placementName.trim() || placementLabels[placementType],
          type: placementType,
          ...(placementType === 'geo-fence' ? { radiusMeters } : {}),
          ...(coordinates
            ? { coordinates }
            : { identifier: placementIdentifier.trim() }),
          metadata: {},
        },
      ],
    });
    setError(null);
    setPlacementName('');
    setPlacementIdentifier('');
  };

  const removePlacement = (id) => {
    setDraft({
      ...draft,
      anchors: (draft.anchors || []).filter((anchor) => anchor.id !== id),
    });
  };

  const submit = async (status) => {
    if (composerBusy) {
      setError('Please wait for your media or suggestion to finish.');
      return;
    }
    const nextDraft = withSelectedResource(
      draft,
      placementType,
      placementIdentifier,
      placementName.trim() || placementLabels[placementType]
    );
    const validationError = validateLifeObjectDraft(nextDraft);
    if (validationError) {
      setError(validationError);
      return;
    }
    const notificationError =
      status === 'active' && pushDeliveryError(delivery, nextDraft.lifecycle);
    if (notificationError) {
      setError(notificationError);
      setPlacementType('push');
      setActiveStep('placements');
      return;
    }
    setError(null);
    try {
      await onSave(
        {
          ...nextDraft,
          metadata: { ...nextDraft.metadata, browserPush: delivery },
          lifecycle: { ...nextDraft.lifecycle, status },
        },
        delivery
      );
    } catch (saveError) {
      // A notification failure must not turn a retry into a second LifeObject.
      if (saveError.savedObject)
        setDraft((current) => ({ ...current, id: saveError.savedObject.id }));
      setError(saveError.message);
    }
  };

  const [composerBusy, setComposerBusy] = React.useState(false);

  return (
    <Dialog
      open={open}
      onClose={composerBusy ? undefined : onClose}
      fullScreen
      disableEnforceFocus
      PaperProps={{
        className: 'lt-authoring-paper',
        sx: {
          bgcolor: lifeTaggerDesign.color.paper,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, md: 4 },
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#FFFCF7',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            onClick={onClose}
            disabled={composerBusy}
            startIcon={<ArrowBackRoundedIcon />}
            sx={{
              color: '#294438',
              minWidth: 150,
              justifyContent: 'flex-start',
            }}
          >
            Collection
          </Button>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <input
              className="lt-document-title"
              aria-label="LifeObject title"
              placeholder="Untitled LifeObject"
              value={draft.name}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
            />
            <Typography sx={{ color: '#706C65', fontSize: 12 }}>
              {saving ? 'Saving…' : 'Ready to save'}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 2,
              py: 0.75,
              borderRadius: 99,
              bgcolor:
                draft.lifecycle.status === 'active' ? '#E4EFE8' : '#FFF0D8',
              color:
                draft.lifecycle.status === 'active' ? '#294438' : '#8C531D',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {draft.lifecycle.status === 'active' ? 'Published' : 'Draft'}
          </Box>
          <Button
            variant="outlined"
            disabled={saving || composerBusy}
            onClick={() => submit('draft')}
            sx={{
              borderRadius: '13px',
              borderColor: '#D8D1C5',
              color: '#1A1C1B',
              px: 3,
            }}
          >
            Save draft
          </Button>
          <Button
            variant="contained"
            onClick={() => submit('active')}
            disabled={saving || composerBusy}
            sx={{ borderRadius: '13px', bgcolor: '#E96842', px: 3 }}
          >
            {delivery.mode === 'now'
              ? 'Publish & send'
              : delivery.mode === 'later'
              ? 'Publish & schedule'
              : 'Publish LifeObject'}
          </Button>
          <IconButton
            aria-label="Close editor"
            disabled={composerBusy}
            onClick={onClose}
            sx={{ display: { md: 'none' } }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            px: { xs: 2, md: 5 },
            py: 2,
            bgcolor: '#F4F0E8',
            overflowX: 'auto',
          }}
        >
          {[
            ['content', 'Content'],
            ['audience', 'Audience'],
            ['placements', 'Discovery'],
            ['review', 'Review'],
          ].map(([value, label]) => (
            <Button
              key={value}
              onClick={() => setActiveStep(value)}
              sx={{
                minWidth: 110,
                borderRadius: '12px',
                bgcolor: activeStep === value ? '#FFD9C3' : 'transparent',
                color: activeStep === value ? '#201D1B' : '#706C65',
                fontWeight: activeStep === value ? 600 : 400,
              }}
            >
              {label}
            </Button>
          ))}
        </Stack>
        <Grid
          container
          sx={{ minHeight: 'calc(100vh - 145px)', bgcolor: '#F4F0E8' }}
        >
          <Grid
            item
            xs={12}
            lg={activeStep === 'content' ? 12 : 9}
            sx={{ p: { xs: 2, md: activeStep === 'content' ? 2 : 5 } }}
          >
            <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
              <Stack spacing={3}>
                {error && <Alert severity="warning">{error}</Alert>}
                <Box
                  sx={{
                    display: activeStep === 'content' ? 'block' : 'none',
                  }}
                >
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Button
                      variant={!editingLifeBot ? 'contained' : 'outlined'}
                      disabled={composerBusy}
                      onClick={() => setEditingLifeBot(false)}
                    >
                      Content
                    </Button>
                    <Button
                      variant={editingLifeBot ? 'contained' : 'outlined'}
                      disabled={composerBusy}
                      onClick={() => setEditingLifeBot(true)}
                    >
                      LifeBot
                    </Button>
                  </Stack>
                  {editingLifeBot ? (
                    <LifeBotSetup
                      onBusyChange={setComposerBusy}
                      lifeObjectId={draft.id || lifeObject?.id}
                      configuration={draft.chatbot}
                      ensureLifeObject={async (botName) => {
                        const name = draft.name.trim() || botName?.trim();
                        if (!name)
                          throw new Error(
                            'Give your LifeBot a name before creating it.'
                          );
                        const saved = await lifeTaggerApi.createLifeObject({
                          ...draft,
                          name,
                          lifecycle: { ...draft.lifecycle, status: 'draft' },
                        });
                        setDraft((current) => ({
                          ...current,
                          id: saved.id,
                          name,
                        }));
                        return saved.id;
                      }}
                      onChange={(chatbot) =>
                        setDraft((current) => ({ ...current, chatbot }))
                      }
                    />
                  ) : (
                    <LifeObjectComposer
                      title={draft.name}
                      content={draft.content}
                      onChange={(content) =>
                        setDraft((current) => ({ ...current, content }))
                      }
                      onBusy={setComposerBusy}
                    />
                  )}

                  <Box
                    component="details"
                    className="lt-attachment-drawer"
                    sx={{ mt: 3 }}
                  >
                    <summary>
                      <strong>Additional content</strong>{' '}
                      <span>
                        {draft.content.length} sections · expand your experience
                      </span>
                    </summary>
                    <Stack
                      className="lt-attachments-heading"
                      direction={{ xs: 'column', sm: 'row' }}
                      alignItems={{ sm: 'center' }}
                      justifyContent="space-between"
                      spacing={2}
                      sx={{ mb: 2 }}
                    >
                      <Typography
                        sx={{
                          fontFamily: lifeTaggerDesign.type.headline,
                          fontSize: 22,
                          fontWeight: 600,
                        }}
                      >
                        There’s more to the story.
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {['image', 'audio', 'video', 'pdf', 'url-embed'].map(
                          (type) => (
                            <IconButton
                              key={type}
                              aria-label={`Add ${type}`}
                              title={type === 'url-embed' ? 'Web link' : type}
                              aria-pressed={mediaType === type}
                              onClick={() => setMediaType(type)}
                              sx={{
                                border: '1px solid',
                                borderColor:
                                  mediaType === type
                                    ? 'text.primary'
                                    : 'divider',
                                bgcolor:
                                  mediaType === type
                                    ? 'rgba(41,68,56,.08)'
                                    : 'transparent',
                              }}
                            >
                              {contentIcons[type]}
                            </IconButton>
                          )
                        )}
                      </Stack>
                    </Stack>

                    <Stack
                      className="lt-attachment-cards"
                      spacing={1.25}
                      sx={{ mb: 2.5 }}
                    >
                      {draft.content.length === 0 && (
                        <Box
                          sx={{
                            py: 4,
                            px: 3,
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 3,
                            textAlign: 'center',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: lifeTaggerDesign.type.headline,
                              fontSize: '1.2rem',
                            }}
                          >
                            Build the experience in the order people should
                            discover it.
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Add an image, audio, video, PDF, or connected web
                            experience.
                          </Typography>
                        </Box>
                      )}
                      {draft.content.map((item, index) => (
                        <Stack
                          className="lt-attachment-card"
                          key={`${item.type}-${index}`}
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                          sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: '18px',
                            bgcolor: '#FFFCF7',
                          }}
                        >
                          <Box
                            className={`lt-attachment-art lt-attachment-art-${item.type}`}
                            sx={{
                              width: 48,
                              height: 48,
                              display: 'grid',
                              placeItems: 'center',
                              borderRadius: '50%',
                              bgcolor: 'rgba(233,104,66,.12)',
                              color: lifeTaggerDesign.color.actionDark,
                            }}
                          >
                            {item.type === 'image' &&
                            (item.assetPreviewUrl || item.url) ? (
                              <>
                                <ImageRoundedIcon />
                                <img
                                  src={item.assetPreviewUrl || item.url}
                                  alt={item.label || 'Attached image'}
                                  onError={(event) => {
                                    event.currentTarget.style.display = 'none';
                                  }}
                                />
                              </>
                            ) : item.type === 'html' || item.type === 'text' ? (
                              <span className="lt-attachment-type-art">Aa</span>
                            ) : (
                              contentIcons[item.type] || <LinkRoundedIcon />
                            )}
                            <span className="lt-attachment-number">
                              {String(index + 1).padStart(2, '0')}
                            </span>
                          </Box>
                          <Box
                            className="lt-attachment-description"
                            sx={{ flex: 1, minWidth: 0 }}
                          >
                            <Typography className="lt-attachment-kind">
                              {item.type === 'html' || item.type === 'text'
                                ? 'ON THE CANVAS'
                                : item.type === 'url-embed'
                                ? 'WEB EXPERIENCE'
                                : item.type.toUpperCase()}
                            </Typography>
                            <Typography fontWeight={700} noWrap>
                              {item.label ||
                                (item.type === 'html'
                                  ? 'Your designed page'
                                  : item.type === 'text'
                                  ? 'Your words'
                                  : item.url?.split('/').pop()?.split('?')[0] ||
                                    `${item.type} attachment`)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {item.type === 'html' || item.type === 'text'
                                ? 'Edit directly in the canvas above.'
                                : 'Part of this LifeObject · shared discovery settings'}
                            </Typography>
                          </Box>
                          <IconButton
                            aria-label={`Move ${item.type} up`}
                            size="small"
                            disabled={index === 0}
                            onClick={() => moveMedia(index, -1)}
                          >
                            <ArrowUpwardRoundedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            aria-label={`Move ${item.type} down`}
                            size="small"
                            disabled={index === draft.content.length - 1}
                            onClick={() => moveMedia(index, 1)}
                          >
                            <ArrowDownwardRoundedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            aria-label={`Remove ${item.type}`}
                            color="error"
                            size="small"
                            onClick={() => removeMedia(index)}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>

                    <Stack
                      className="lt-attachment-add"
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1}
                    >
                      <Button
                        component="label"
                        variant="contained"
                        startIcon={<CloudUploadRoundedIcon />}
                        disabled={
                          uploading ||
                          saving ||
                          !apiCapabilities.managedAssetUpload
                        }
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {uploading ? 'Uploading…' : 'Choose a file'}
                        <input
                          hidden
                          type="file"
                          accept="image/*,video/*,audio/*,application/pdf"
                          onChange={uploadMedia}
                        />
                      </Button>
                      <TextField
                        inputProps={{ 'aria-label': 'Additional content URL' }}
                        fullWidth
                        size="small"
                        placeholder={`Paste ${
                          mediaType === 'url-embed'
                            ? 'web experience'
                            : mediaType
                        } URL`}
                        value={mediaUrl}
                        onChange={(event) => setMediaUrl(event.target.value)}
                      />
                      <Button
                        variant="outlined"
                        onClick={addMedia}
                        disabled={!mediaUrl.trim() || uploading || saving}
                      >
                        Add link
                      </Button>
                    </Stack>
                    <Typography className="lt-attachment-note">
                      Images, film, sound, documents, or a link. One LifeObject,
                      in the order you choose.
                    </Typography>
                    {!apiCapabilities.managedAssetUpload && (
                      <Typography
                        color="text.secondary"
                        variant="caption"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        Direct uploads are coming online. External media links
                        work now.
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{ display: activeStep === 'audience' ? 'block' : 'none' }}
                >
                  <Typography
                    sx={{ color: '#E96842', fontSize: 11, fontWeight: 600 }}
                  >
                    CHOOSE WHO CAN DISCOVER IT
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1.5,
                      fontFamily: lifeTaggerDesign.type.headline,
                      fontSize: 36,
                      fontWeight: 600,
                    }}
                  >
                    Who is this for?
                  </Typography>
                  <Typography sx={{ color: '#706C65', fontSize: 14, mb: 3 }}>
                    Choose who can discover this. Every attached discovery
                    requirement must also match.
                  </Typography>
                  <AudiencePicker
                    audience={draft.audience}
                    onAudienceData={setAudienceData}
                    onChange={(audience) => setDraft({ ...draft, audience })}
                  />
                </Box>
                <Box
                  sx={{
                    display: activeStep === 'placements' ? 'block' : 'none',
                  }}
                >
                  <Typography
                    sx={{ color: '#E96842', fontSize: 11, fontWeight: 600 }}
                  >
                    PLACE IT IN CONTEXT
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1.5,
                      fontFamily: lifeTaggerDesign.type.headline,
                      fontSize: 36,
                      fontWeight: 600,
                    }}
                  >
                    How can it be discovered?
                  </Typography>
                  <Typography color="text.secondary">
                    Choose how people find this LifeObject. Every attached
                    discovery requirement must match.
                  </Typography>
                  {!apiCapabilities.anchorPersistence && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      LifeCodes, locations, nearby devices, and recognized audio
                      use the existing compatibility fields until Anchor
                      services deploy.
                    </Alert>
                  )}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 5, mb: 1.5 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: lifeTaggerDesign.type.headline,
                        fontSize: 22,
                        fontWeight: 600,
                      }}
                    >
                      Discovery requirements
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(draft.anchors || []).length +
                        (delivery.mode !== 'off' ? 1 : 0) +
                        (draft.lifecycle.startAt || draft.lifecycle.endAt
                          ? 1
                          : 0)}{' '}
                      configured
                    </Typography>
                  </Stack>
                  <Stack spacing={1.25} sx={{ mb: 5 }}>
                    {(draft.anchors || []).length === 0 &&
                      delivery.mode === 'off' &&
                      !draft.lifecycle.startAt &&
                      !draft.lifecycle.endAt && (
                        <Typography variant="body2" color="text.secondary">
                          No discovery requirements yet. Choose one below to
                          begin.
                        </Typography>
                      )}
                    {delivery.mode !== 'off' && (
                      <Button
                        variant="outlined"
                        onClick={() => setPlacementType('push')}
                      >
                        Push notification ·{' '}
                        {delivery.mode === 'now'
                          ? 'Send when published'
                          : `Scheduled ${
                              delivery.time
                                ? new Date(delivery.time).toLocaleString()
                                : '— choose a time'
                            }`}
                      </Button>
                    )}
                    {(draft.lifecycle.startAt || draft.lifecycle.endAt) && (
                      <Button
                        variant="outlined"
                        onClick={() => setPlacementType('availability')}
                      >
                        Time & availability ·{' '}
                        {draft.lifecycle.startAt
                          ? new Date(draft.lifecycle.startAt).toLocaleString()
                          : 'Available now'}
                        {draft.lifecycle.endAt
                          ? ` to ${new Date(
                              draft.lifecycle.endAt
                            ).toLocaleString()}`
                          : ''}
                        {draft.lifecycle.recurrence !== 'none'
                          ? ` · ${draft.lifecycle.recurrence}`
                          : ''}
                      </Button>
                    )}
                    {(draft.anchors || []).map((anchor) => (
                      <Stack
                        key={anchor.id}
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{
                          p: 2,
                          bgcolor: '#FFFCF7',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: '20px',
                        }}
                      >
                        <Box
                          sx={{
                            color: lifeTaggerDesign.color.moss,
                            display: 'flex',
                          }}
                        >
                          {placementIcons[anchor.type] || (
                            <SensorsRoundedIcon />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={700}>
                            {anchor.name}
                          </Typography>
                          <Typography
                            color="text.secondary"
                            variant="body2"
                            noWrap
                          >
                            {anchor.identifier ||
                              (anchor.coordinates
                                ? `${anchor.coordinates.latitude}, ${
                                    anchor.coordinates.longitude
                                  }${
                                    anchor.radiusMeters
                                      ? ` · ${anchor.radiusMeters} meter radius`
                                      : ''
                                  }`
                                : '')}
                          </Typography>
                        </Box>
                        <Button
                          color="error"
                          size="small"
                          onClick={() => removePlacement(anchor.id)}
                        >
                          Remove
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                  <Typography
                    variant="h6"
                    sx={{ fontFamily: lifeTaggerDesign.type.headline, mb: 1.5 }}
                  >
                    Add a discovery requirement
                  </Typography>
                  <Grid container spacing={1.5} sx={{ mb: 3 }}>
                    {Object.entries(placementLabels).map(([value, label]) => {
                      const supported =
                        ['push', 'availability'].includes(value) ||
                        apiCapabilities.supportedAnchorTypes.includes(value);
                      return (
                        <Grid item xs={12} sm={6} md={4} key={value}>
                          <Button
                            fullWidth
                            disabled={!supported}
                            onClick={() => selectDiscoveryOption(value)}
                            sx={{
                              p: 2,
                              minHeight: 112,
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              border: '1px solid',
                              borderColor:
                                placementType === value
                                  ? 'text.primary'
                                  : 'divider',
                              borderRadius: 3,
                              bgcolor:
                                placementType === value
                                  ? 'rgba(233,104,66,.08)'
                                  : 'rgba(255,255,255,.35)',
                              color: 'text.primary',
                            }}
                          >
                            <Stack spacing={0.75} alignItems="flex-start">
                              <Box
                                sx={{
                                  color: lifeTaggerDesign.color.actionDark,
                                  display: 'flex',
                                }}
                              >
                                {value === 'push' ? (
                                  <NotificationsActiveRoundedIcon />
                                ) : value === 'availability' ? (
                                  <ScheduleRoundedIcon />
                                ) : (
                                  placementIcons[value] || (
                                    <SensorsRoundedIcon />
                                  )
                                )}
                              </Box>
                              <Typography fontWeight={700}>{label}</Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ lineHeight: 1.3 }}
                              >
                                {placementGuidance[value]}
                              </Typography>
                            </Stack>
                          </Button>
                        </Grid>
                      );
                    })}
                  </Grid>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {placementGuidance[placementType]}
                  </Typography>
                  <Box
                    sx={{
                      p: 2.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 3,
                      bgcolor: 'rgba(255,255,255,.42)',
                      display: ['push', 'availability'].includes(placementType)
                        ? 'none'
                        : 'block',
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1.5}
                    >
                      {reusableResource ? (
                        <TextField
                          fullWidth
                          select
                          label={
                            resourcesLoading
                              ? 'Loading resources…'
                              : `Choose ${placementLabels[placementType]}`
                          }
                          disabled={resourcesLoading}
                          value={placementIdentifier}
                          onChange={(event) => {
                            setPlacementIdentifier(event.target.value);
                            setPlacementName(
                              resources.find(
                                (item) => item.identifier === event.target.value
                              )?.name || ''
                            );
                          }}
                        >
                          {resources.map((item) => (
                            <MenuItem
                              key={item.identifier}
                              value={item.identifier}
                            >
                              {item.name}
                            </MenuItem>
                          ))}
                          {!resources.length && (
                            <MenuItem disabled value="">
                              No registered resources yet
                            </MenuItem>
                          )}
                        </TextField>
                      ) : (
                        <>
                          <TextField
                            fullWidth
                            label="Display name"
                            placeholder={placementLabels[placementType]}
                            value={placementName}
                            onChange={(event) =>
                              setPlacementName(event.target.value)
                            }
                          />
                          <TextField
                            fullWidth
                            label={
                              ['geo-point', 'geo-fence'].includes(placementType)
                                ? 'Latitude, longitude'
                                : 'Code or identifier'
                            }
                            value={placementIdentifier}
                            onChange={(event) =>
                              setPlacementIdentifier(event.target.value)
                            }
                          />
                        </>
                      )}
                      {placementType === 'geo-fence' && (
                        <TextField
                          label="Radius in meters"
                          type="number"
                          value={placementRadius}
                          onChange={(event) =>
                            setPlacementRadius(event.target.value)
                          }
                          inputProps={{ min: 1 }}
                          sx={{ minWidth: 150 }}
                        />
                      )}
                      <Button variant="outlined" onClick={addPlacement}>
                        Add requirement
                      </Button>
                    </Stack>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display:
                      activeStep === 'review' ||
                      (activeStep === 'placements' &&
                        ['push', 'availability'].includes(placementType))
                        ? 'block'
                        : 'none',
                  }}
                >
                  <Typography
                    sx={{ color: '#E96842', fontSize: 11, fontWeight: 600 }}
                  >
                    {activeStep === 'review'
                      ? 'READY TO SHARE'
                      : 'DISCOVERY SETTINGS'}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1.5,
                      fontFamily: lifeTaggerDesign.type.headline,
                      fontSize: 36,
                      fontWeight: 600,
                    }}
                  >
                    {activeStep === 'review'
                      ? 'Review & publish'
                      : placementLabels[placementType]}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {activeStep === 'review'
                      ? 'Check your audience and discovery settings before publishing.'
                      : placementGuidance[placementType]}
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {audiencePresentation(draft.audience, audienceData).label}:{' '}
                    {audiencePresentation(draft.audience, audienceData).reach}.{' '}
                    Every attached discovery requirement must also match.
                  </Alert>
                  <Box
                    sx={{
                      display:
                        activeStep === 'placements' && placementType === 'push'
                          ? 'block'
                          : 'none',
                    }}
                  >
                    <WebPushTrigger
                      lifeObject={draft}
                      value={delivery}
                      onChange={(next) =>
                        setDelivery({
                          ...next,
                          title: next.title || draft.name,
                        })
                      }
                      disabled={saving || composerBusy}
                    />
                  </Box>
                  <Box
                    sx={{
                      display:
                        activeStep === 'placements' &&
                        placementType === 'availability'
                          ? 'block'
                          : 'none',
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Availability window
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                      Leave dates blank for continuous availability. For a
                      repeating window, choose its first start and end, then how
                      often it repeats. Times are shown in{' '}
                      {Intl.DateTimeFormat().resolvedOptions().timeZone}.
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        label="Starts"
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        value={localDeliveryTime(draft.lifecycle.startAt)}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            lifecycle: {
                              ...draft.lifecycle,
                              startAt: event.target.value
                                ? new Date(event.target.value).toISOString()
                                : null,
                            },
                          })
                        }
                      />
                      <TextField
                        fullWidth
                        label="Ends"
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        value={localDeliveryTime(draft.lifecycle.endAt)}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            lifecycle: {
                              ...draft.lifecycle,
                              endAt: event.target.value
                                ? new Date(event.target.value).toISOString()
                                : null,
                            },
                          })
                        }
                      />
                      <FormControl fullWidth>
                        <InputLabel>Repeats</InputLabel>
                        <Select
                          label="Repeats"
                          value={draft.lifecycle.recurrence}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              lifecycle: {
                                ...draft.lifecycle,
                                recurrence: event.target.value,
                              },
                            })
                          }
                        >
                          <MenuItem value="none">Does not repeat</MenuItem>
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                          <MenuItem value="yearly">Yearly</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                    {draft.lifecycle.recurrence !== 'none' && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Repeats control when this LifeObject is available. A
                        scheduled push notification is sent once, at the time
                        you choose under Push notification.
                      </Alert>
                    )}
                  </Box>
                  {activeStep === 'review' && (
                    <Button onClick={() => setActiveStep('placements')}>
                      Edit discovery settings
                    </Button>
                  )}
                </Box>
              </Stack>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            lg={3}
            sx={{
              display: {
                xs: activeStep === 'content' ? 'none' : 'block',
                lg: activeStep === 'content' ? 'none' : 'block',
              },
              p: { xs: 2, md: 2 },
              bgcolor: '#F4F0E8',
            }}
          >
            {activeStep === 'content' && (
              <Stack
                spacing={2}
                sx={{
                  position: { lg: 'sticky' },
                  top: 24,
                  bgcolor: '#17261F',
                  borderRadius: '28px',
                  p: 2,
                  color: '#fff',
                  minHeight: 700,
                }}
              >
                <Typography
                  sx={{
                    color: '#D6E1D8',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: 1,
                  }}
                >
                  LIVE PREVIEW
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    mx: 'auto',
                    borderRadius: '22px',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      minHeight: 520,
                      bgcolor: '#FFFCF7',
                      overflow: 'hidden',
                    }}
                  >
                    <LifeObjectRenderer lifeObject={draft} compact />
                  </Box>
                </Box>
                <Typography sx={{ mt: 'auto', color: '#A7B5AB', fontSize: 12 }}>
                  People see this after a matching signal resolves.
                </Typography>
              </Stack>
            )}
            {activeStep === 'audience' && (
              <Stack
                spacing={2.5}
                sx={{
                  position: { lg: 'sticky' },
                  top: 24,
                  bgcolor: '#FFFCF7',
                  border: '1px solid #D8D1C5',
                  borderRadius: '24px',
                  p: 4,
                  minHeight: 360,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: lifeTaggerDesign.type.headline,
                    fontSize: 26,
                    fontWeight: 600,
                  }}
                >
                  Audience preview
                </Typography>
                <Typography sx={{ color: '#706C65', fontSize: 14 }}>
                  An encounter must satisfy the audience rules plus any required
                  contextual signals.
                </Typography>
                <Box
                  sx={{
                    alignSelf: 'flex-start',
                    bgcolor: '#E4EFE8',
                    color: '#294438',
                    px: 2,
                    py: 0.75,
                    borderRadius: 99,
                    fontSize: 12,
                  }}
                >
                  {audiencePresentation(draft.audience, audienceData).label}
                </Box>
                <Divider />
                <Typography sx={{ color: '#706C65', fontSize: 11 }}>
                  WHO CAN DISCOVER THIS
                </Typography>
                <Typography
                  sx={{
                    fontFamily: lifeTaggerDesign.type.headline,
                    fontSize: 24,
                    fontWeight: 600,
                  }}
                >
                  {audiencePresentation(draft.audience, audienceData).reach}
                </Typography>
                <Typography sx={{ color: '#706C65', fontSize: 13 }}>
                  Subscribing does not bypass location, time, or any other
                  discovery requirements.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep('placements')}
                  sx={{
                    mt: 'auto',
                    bgcolor: '#E96842',
                    borderRadius: '13px',
                    py: 1.5,
                  }}
                >
                  Continue to signals →
                </Button>
              </Stack>
            )}
            {activeStep === 'placements' && (
              <Stack
                spacing={3}
                sx={{
                  position: { lg: 'sticky' },
                  top: 24,
                  bgcolor: '#17261F',
                  borderRadius: '28px',
                  p: 4,
                  color: '#fff',
                  minHeight: 620,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: lifeTaggerDesign.type.headline,
                    fontSize: 26,
                    fontWeight: 600,
                  }}
                >
                  Resolution preview
                </Typography>
                <Typography sx={{ color: '#B9C5BD', fontSize: 14 }}>
                  Test what a person would discover with the signals currently
                  available.
                </Typography>
                <Typography sx={{ color: '#91A198', fontSize: 10 }}>
                  DETECTED CONTEXT
                </Typography>
                {(draft.anchors || []).length ? (
                  (draft.anchors || []).map((anchor) => (
                    <Stack
                      key={anchor.id}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Typography sx={{ fontSize: 13 }}>
                        {placementLabels[anchor.type] || anchor.type}
                      </Typography>
                      <Typography sx={{ color: '#A7B5AB', fontSize: 12 }}>
                        Available
                      </Typography>
                    </Stack>
                  ))
                ) : (
                  <Typography sx={{ color: '#B9C5BD', fontSize: 13 }}>
                    No signals connected yet.
                  </Typography>
                )}
                <Box
                  sx={{
                    mt: 2,
                    p: 2.5,
                    bgcolor: '#294E3D',
                    borderRadius: '16px',
                  }}
                >
                  <Typography sx={{ color: '#A7B5AB', fontSize: 10 }}>
                    RESOLVED LIFEOBJECT
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1,
                      fontFamily: lifeTaggerDesign.type.headline,
                      fontSize: 20,
                    }}
                  >
                    {draft.name || 'Untitled LifeObject'}
                  </Typography>
                  <Typography sx={{ color: '#B9C5BD', fontSize: 11 }}>
                    {draft.anchors?.length || 0} contributing signals
                  </Typography>
                </Box>
              </Stack>
            )}
            {activeStep === 'review' && (
              <Stack
                spacing={2.5}
                sx={{
                  position: { lg: 'sticky' },
                  top: 24,
                  bgcolor: '#FFFCF7',
                  border: '1px solid #D8D1C5',
                  borderRadius: '24px',
                  p: 4,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: lifeTaggerDesign.type.headline,
                    fontSize: 26,
                    fontWeight: 600,
                  }}
                >
                  Lifecycle preview
                </Typography>
                <Typography sx={{ color: '#706C65', fontSize: 14 }}>
                  {draft.lifecycle.startAt
                    ? `Starts ${new Date(
                        draft.lifecycle.startAt
                      ).toLocaleString()}`
                    : 'Available as soon as it is published.'}
                </Typography>
                <Typography sx={{ color: '#706C65', fontSize: 14 }}>
                  {draft.lifecycle.endAt
                    ? `Ends ${new Date(draft.lifecycle.endAt).toLocaleString()}`
                    : 'No end date.'}
                </Typography>
                <Typography sx={{ color: '#706C65', fontSize: 14 }}>
                  Repeats: {draft.lifecycle.recurrence || 'none'}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => submit('draft')}
                  disabled={saving}
                  sx={{ borderRadius: '13px' }}
                >
                  Save draft
                </Button>
              </Stack>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
